import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, Search } from 'lucide-react'
import { listJobs } from '../../api/jobs'
import Spinner from '../../components/Spinner'
import ErrorBanner from '../../components/ErrorBanner'
import { FilterChips, StatusBadge } from '../../components/PageParts'
import HelpTip from '../../components/HelpTip'
import { JobsGuide } from './JobsGuide'
import { money } from './salesShared'

export const JOB_STATUS = {
  Open: { label: 'Open', tone: 'info' },
  Completed: { label: 'Completed', tone: 'good' },
  Cancelled: { label: 'Cancelled', tone: 'muted' },
}

const FILTERS = [
  { id: 'Open', label: 'Open' },
  { id: 'Completed', label: 'Completed' },
  { id: 'all', label: 'All' },
]

/** "46%" or "–" when nothing has been earned yet. */
export const marginText = (m) => (m == null ? '–' : `${Number(m).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`)

// Every job with what it earned, what it cost and the profit.
export default function JobList() {
  const nav = useNavigate()
  const [rows, setRows] = useState([])
  const [filter, setFilter] = useState('Open')
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    listJobs()
      .then((r) => setRows(Array.isArray(r) ? r : []))
      .catch((e) => setError(e.message || 'Could not load jobs.'))
      .finally(() => setLoading(false))
  }, [])

  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f.id, rows.filter((r) => f.id === 'all' || r.status === f.id).length])), [rows])
  const shown = useMemo(() => {
    const term = search.trim().toLowerCase()
    return rows.filter((r) => (filter === 'all' || r.status === filter)
      && (!term || [r.job_number, r.name, r.customer_name, r.site_address].join(' ').toLowerCase().includes(term)))
  }, [rows, filter, search])
  // Long jobs count as the work is done.
  const asDone = { profit: 'profit_as_done', revenue: 'earned_as_done' }
  const total = (k) => shown.reduce((s, r) => s + Number((asDone[k] && r[asDone[k]] != null ? r[asDone[k]] : r[k]) || 0), 0)

  return (
    <div className="ci-page">
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Jobs <HelpTip text="A job is one piece of work for a customer. Put its quotes, invoices, materials bills and expenses on it, and it shows whether the job made money." /></h1>
            <div className="ci-title-meta">Each piece of work, what it earned, what it cost and the profit.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <Link className="ci-btn ci-btn-primary" to="/jobs/new"><Plus size={15} />New job</Link>
        </div>
      </div>

      <JobsGuide />

      <div className="ci-toolbar">
        <label className="ci-search" aria-label="Search jobs">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Job, customer or address…" />
        </label>
      </div>
      <FilterChips options={FILTERS.map((f) => ({ ...f, count: counts[f.id] }))} value={filter} onChange={setFilter} />
      {error && <ErrorBanner message={error} />}

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table">
            <thead>
              <tr>
                <th>Job</th>
                <th className="hide-sm">Customer</th>
                <th className="center hide-sm">Status</th>
                <th className="num hide-sm">Quoted <HelpTip text="The total of the job’s quotes (not void)." /></th>
                <th className="num">Earned <HelpTip text="What the job’s posted invoices count as income: invoiced less sales tax, discounts and credits. For a long job, what the work so far has earned." /></th>
                <th className="num hide-sm">Costs <HelpTip text="The job’s bills and expenses, and the cost of your own stock sold on its invoices." /></th>
                <th className="num">Profit <HelpTip text="Earned minus costs. For a long job, profit as the work is done (with how far along it is)." /></th>
                <th className="num hide-sm">Margin <HelpTip text="Profit as a share of earned. 46% means 46 of every 100 earned is profit." /></th>
                <th className="num hide-sm">Owed <HelpTip text="Invoiced to the customer but not yet paid or credited." /></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9"><Spinner label="Loading jobs…" /></td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan="9"><div className="ci-empty">{rows.length ? 'No jobs match.' : 'No jobs yet. Add one for each piece of work, such as “Smith bathroom”.'}</div></td></tr>
              ) : shown.map((r) => (
                <tr key={r.job_id} className="row-link" onClick={() => nav(`/jobs/${r.job_id}`)}>
                  <td>
                    <Link className="ci-strong" to={`/jobs/${r.job_id}`} onClick={(e) => e.stopPropagation()}>{r.name}</Link>
                    <span className="cell-sub">{r.job_number}{r.site_address ? ` · ${r.site_address}` : ''}</span>
                  </td>
                  <td className="hide-sm">{r.customer_name || '–'}</td>
                  <td className="center hide-sm"><StatusBadge label={JOB_STATUS[r.status]?.label || r.status} tone={JOB_STATUS[r.status]?.tone} /></td>
                  <td className="num hide-sm ci-muted-cell">{r.quoted ? money(r.quoted) : '–'}</td>
                  <td className="num">{money(r.earned_as_done ?? r.revenue)}</td>
                  <td className="num hide-sm">{money(r.costs)}</td>
                  {r.profit_as_done != null ? (
                    <>
                      <td className={`num ci-total${r.profit_as_done < 0 ? ' ci-overdue-cell' : ''}`}>{money(r.profit_as_done)}<span className="cell-sub">{r.percent_done}% done</span></td>
                      <td className="num hide-sm">{marginText(r.margin_as_done)}</td>
                    </>
                  ) : (
                    <>
                      <td className={`num ci-total${r.profit < 0 ? ' ci-overdue-cell' : ''}`}>{money(r.profit)}</td>
                      <td className="num hide-sm">{marginText(r.margin)}</td>
                    </>
                  )}
                  <td className="num hide-sm">{r.owed > 0.004 ? money(r.owed) : '–'}</td>
                </tr>
              ))}
            </tbody>
            {shown.length > 1 && (
              <tfoot>
                <tr>
                  <td className="ci-strong">Total</td>
                  <td className="hide-sm" />
                  <td className="hide-sm" />
                  <td className="num hide-sm">{money(total('quoted'))}</td>
                  <td className="num">{money(total('revenue'))}</td>
                  <td className="num hide-sm">{money(total('costs'))}</td>
                  <td className="num ci-total">{money(total('profit'))}</td>
                  <td className="hide-sm" />
                  <td className="num hide-sm">{money(total('owed'))}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  )
}
